import { CharacterClassId, CharacterRole, rosterEntry } from '../config/character-roster';
import {
  HP_TINT, STAMINA_TINT, RESOURCE_TINT, RESOURCE_GLYPH,
  PLAQUE, PLAQUE_SMALL, LABEL, chunkyBar, portraitDisc, glyphUrl, portraitUrl,
} from './hud-theme';
import { t, N_, getIntlLocale } from '../i18n/i18n';

/**
 * The game's HUD: your own vitals, your party, your target, and a reticle.
 *
 * User reports (2026-08-24): "the interface for my abilities, HP, mana, and
 * energy is not UI-friendly at all" and "I do not understand where I am
 * hitting or how much damage I am dealing."
 *
 * Before this, a player's health and stamina existed on screen only as
 * `hp: 30  stamina: 105` inside `DebugHud` — a monospace playtest overlay
 * whose own file comment says it is "not game UI, a playtesting instrument".
 * It occupied the top-left ninth of the screen and shared that space with a
 * keybind legend and a scrolling combat log. The resource bar lived on the
 * far side of the screen, at the action bar. Nothing showed the party at all,
 * and nothing showed what you were fighting.
 *
 * Four panels, each answering one question the reports asked:
 *
 * - **Player frame** (bottom-left): am I in trouble? HP, stamina, resource.
 * - **Party frames** (top-right): is my group alive, and which one is the
 *   healer? This is also the answer to a question left open in an earlier
 *   session — friendly fire and squad-only healing were enforced invisibly,
 *   with no way to tell a squadmate from a rival. `game-concept.md`'s
 *   anti-pillar rejects floating nameplates over bodies; a party list is not
 *   one, and it puts nothing into the world.
 * - **Target frame** (top-centre): what am I hitting, and how close is it to
 *   dead?
 * - **Reticle** (centre): where "forward" is. The literal answer to "I do not
 *   understand where I am hitting" in a game with a shoulder-offset camera,
 *   where screen-centre is not obviously the character's facing.
 *
 * ## Restyled to match the game (2026-09-01)
 *
 * User: "All the assets in the HUD should match the game's art style — more
 * cartoonish, similar to the characters and maps."
 *
 * Every panel here was a black box with a hairline ring and flat rectangular
 * bars — correct information, drawn in a different game's language from the
 * soft low-poly dungeon behind it. The layout and the thresholds are
 * unchanged; what changed is that it is now drawn rather than specified. See
 * `hud-theme.ts` for the rules and why each one is there.
 *
 * Two things stopped being text and became pictures:
 *
 * - **The class portrait.** The lobby already had four painted busts in
 *   exactly the right style, used once at character select and never again.
 *   Putting them in the player and party frames cost nothing, and a squad
 *   list of faces is scannable in a way a list of names is not.
 * - **Bar glyphs.** A heart on health and a flame/droplet/bolt on the
 *   resource, so which bar is which survives colour-blindness and a glance.
 *
 * Plain DOM + Tailwind, same as every other panel in `engine/ui/`. Mobile
 * first per CLAUDE.md: nothing here is interactive, so there are no tap
 * targets to size, but the panels sit inside `env(safe-area-inset-*)` padding
 * so a notch never clips a health bar.
 */

export interface HudVitals {
  hp: number;
  hpMax: number;
  stamina: number;
  staminaMax: number;
  resource: number;
  resourceMax: number;
  resourceKind: string;
  characterClass: CharacterClassId;
  nickname: string;
}

export interface HudPartyMember {
  sessionId: string;
  nickname: string;
  characterClass: CharacterClassId;
  role: CharacterRole;
  hp: number;
  hpMax: number;
  isDead: boolean;
  /**
   * Seconds left to reach this body, or null when there is no window.
   *
   * `isDead` alone used to be the whole story, and it read `DOWN` forever: a
   * finalised body stays in the world by design, so a squadmate lost in the
   * first minute was still listed DOWN at the end of the run. The player has
   * no way to tell "run, you have eleven seconds" from "they are gone", which
   * is the difference between a rescue and a wasted trip across the dungeon.
   */
  reviveSecondsLeft: number | null;
  isLocal: boolean;
  /** Distance in metres — a squadmate who has fallen behind is worth seeing before they die out of range. */
  distanceM: number;
  /** Ally targeting (2026-10-16) — this row is the current target. */
  isTargeted?: boolean;
}

export interface HudTarget {
  name: string;
  hp: number;
  hpMax: number;
  distanceM: number;
  isHostile: boolean;
  /**
   * 2026-10-16 — the ability most likely to be pressed next cannot reach it.
   * Said on the frame, in words, before a press is refused.
   */
  outOfRange?: boolean;
}

/** One glyph per role, so the party list reads at a glance without colour alone carrying it. */
const ROLE_GLYPH: Record<CharacterRole, string> = { TANK: '◆', HEALER: '✚', DPS: '⚔' };
/** Role and resource names as the frames print them — translated where drawn. */
const ROLE_LABEL: Record<CharacterRole, string> = { TANK: N_('TANK'), HEALER: N_('HEALER'), DPS: N_('DPS') };
const RESOURCE_LABEL: Record<string, string> = { MANA: N_('MANA'), RAGE: N_('RAGE'), ENERGY: N_('ENERGY') };

const ROLE_TINT: Record<CharacterRole, string> = {
  TANK: 'text-[#8ed0ff]',
  HEALER: 'text-[#8ff0b0]',
  DPS: 'text-[#ffb877]',
};

/**
 * A bar's fill width, as a percentage string.
 *
 * Clamped, and that is the point rather than defensiveness: the reported
 * "110 / 100" frame also drew a fill 110% as wide as its track. The
 * denominator bug is fixed at the source (`PlayerState.hpMax` is synced now),
 * but a fill that can escape its own track will do it again for the next
 * reason — a momentary overheal, a stale snapshot, a future buff. `hpMax <= 0`
 * (an entity whose state has not arrived yet) reads as empty, not as NaN.
 */
function fillWidth(value: number, max: number): string {
  if (!(max > 0)) return '0%';
  return `${Math.min(100, Math.max(0, (value / max) * 100))}%`;
}

/** Green above half, amber above a quarter, red below — the same thresholds the monster health-bar sprite uses, so one body and its party row never disagree. */
function hpTint(fraction: number): string {
  return fraction > 0.5 ? HP_TINT.healthy : fraction > 0.25 ? HP_TINT.hurt : HP_TINT.critical;
}

export class CombatHud {
  private readonly reticle: HTMLDivElement;

  private readonly playerPanel: HTMLDivElement;
  private readonly playerPortrait: HTMLImageElement;
  private readonly playerName: HTMLDivElement;
  private readonly roleBadge: HTMLDivElement;
  private readonly hpFill: HTMLDivElement;
  private readonly hpLabel: HTMLDivElement;
  private readonly staminaFill: HTMLDivElement;
  private readonly resourceFill: HTMLDivElement;
  private readonly resourceLabel: HTMLDivElement;
  private readonly resourceGlyph: HTMLImageElement;

  private readonly partyPanel: HTMLDivElement;
  private readonly partyRows = new Map<string, {
    row: HTMLDivElement; name: HTMLDivElement; glyph: HTMLDivElement;
    portrait: HTMLImageElement; fill: HTMLDivElement; hp: HTMLDivElement; skull: HTMLImageElement;
  }>();

  private readonly targetPanel: HTMLDivElement;
  private readonly targetName: HTMLDivElement;
  private readonly targetFill: HTMLDivElement;
  private readonly targetHp: HTMLDivElement;
  private readonly targetRange: HTMLDivElement;
  /** Fires with a squadmate's session id when their party row is clicked or tapped. */
  onSelectMember: (sessionId: string) => void = () => {};

  constructor(container: HTMLElement) {
    // ── Reticle ──────────────────────────────────────────────────────────
    // A drawn crosshair rather than a CSS dot-and-ring. It is the one HUD
    // element that is always over the world and never over a panel, so it is
    // the one that most needed to stop looking like a browser artefact.
    this.reticle = document.createElement('div');
    this.reticle.className = 'fixed left-1/2 top-1/2 z-[850] -translate-x-1/2 -translate-y-1/2 pointer-events-none';
    const reticleImg = document.createElement('img');
    reticleImg.src = glyphUrl('reticle');
    reticleImg.alt = '';
    reticleImg.draggable = false;
    reticleImg.width = 128;
    reticleImg.height = 128;
    reticleImg.className = 'w-7 h-7 select-none opacity-90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]';
    this.reticle.appendChild(reticleImg);
    container.appendChild(this.reticle);

    // ── Player frame ─────────────────────────────────────────────────────
    this.playerPanel = document.createElement('div');
    // The bottom edge is responsive because the action bar wraps to two rows
    // below ~430px and sat straight on top of this panel — vitals unreadable
    // on a phone, which is the platform CLAUDE.md puts first. Lifted clear of
    // the bar there, back in the corner once the bar fits on one row.
    this.playerPanel.dataset.hud = 'player';
    this.playerPanel.className = 'fixed z-[880] pointer-events-none left-[max(0.75rem,env(safe-area-inset-left))] '
      + 'bottom-[9.25rem] sm:bottom-[max(0.75rem,env(safe-area-inset-bottom))] w-[15rem] max-w-[62vw] '
      + `${PLAQUE} px-2.5 py-2 flex items-center gap-2.5`;

    const disc = portraitDisc('w-14 h-14');
    this.playerPortrait = disc.img;

    // The role badge hangs off the portrait rather than sitting in the name
    // line: it is a property of the person, and a player checking "am I the
    // healer here" is looking at the face.
    this.roleBadge = document.createElement('div');
    this.roleBadge.className = 'absolute -bottom-1 -right-1 w-5 h-5 rounded-full border border-[#514132] '
      + 'bg-[#2b2219] flex items-center justify-center text-[11px] leading-none';
    disc.wrap.appendChild(this.roleBadge);

    const bars = document.createElement('div');
    bars.className = 'flex-1 min-w-0 flex flex-col gap-1.5';

    this.playerName = document.createElement('div');
    this.playerName.className = `text-[11px] font-semibold tracking-wide truncate ${LABEL}`;

    const hpBar = chunkyBar('h-[1.15rem]', 'heart');
    this.hpFill = hpBar.fill;
    this.hpLabel = hpBar.label;
    this.hpLabel.classList.add('text-[10px]', 'pl-3');

    // Stamina is thinner than HP on purpose: running out of stamina costs you
    // a swing, running out of HP costs you the run. The hierarchy should be
    // visible before either number is read.
    // h-2.5, not the h-1.5 it was: a 2px ink border on each side of a 6px bar
    // leaves 2px of fill, which read as an empty groove between the other two
    // bars rather than as a bar. Still the thinnest of the three.
    const staminaBar = chunkyBar('h-2.5');
    this.staminaFill = staminaBar.fill;

    const resourceBar = chunkyBar('h-3.5', 'droplet');
    this.resourceFill = resourceBar.fill;
    this.resourceLabel = resourceBar.label;
    this.resourceLabel.classList.add('text-[9px]', 'tracking-wider', 'pl-3');
    this.resourceGlyph = resourceBar.wrap.querySelector('img')!;

    bars.append(this.playerName, hpBar.wrap, staminaBar.wrap, resourceBar.wrap);
    this.playerPanel.append(disc.wrap, bars);
    container.appendChild(this.playerPanel);

    // ── Party frames ─────────────────────────────────────────────────────
    this.partyPanel = document.createElement('div');
    // Top-RIGHT, not top-left. Found live: the top-left already holds the
    // objective line (`debug-hud.ts`), and two `fixed` panels claiming the same
    // corner drew straight through each other — the party rows rendered
    // underneath the objective text, both illegible. The top-right corner is
    // the only one free: bottom-left is the player frame, bottom-centre the
    // action bar, bottom-right the loot panel, top-centre the target frame.
    // Below the run-menu button, which took this corner on 2026-09-03 when it
    // was moved out of the centre column it shared with the compass. 3.75rem
    // clears its 44px plus the 0.5rem it hangs from.
    // The panel stays pointer-events-none; each row opts back in (2026-10-16)
    // so a squadmate can be clicked or tapped as a heal target without the
    // gaps between rows eating presses meant for the world.
    this.partyPanel.className = 'fixed z-[880] pointer-events-none right-[max(0.75rem,env(safe-area-inset-right))] '
      + 'top-[max(3.75rem,calc(env(safe-area-inset-top)+3.25rem))] w-[13rem] max-w-[46vw] flex flex-col gap-1.5 hidden';
    container.appendChild(this.partyPanel);

    // ── Target frame ─────────────────────────────────────────────────────
    this.targetPanel = document.createElement('div');
    this.targetPanel.className = 'fixed z-[880] pointer-events-none left-1/2 -translate-x-1/2 '
      + 'top-[max(3.5rem,calc(env(safe-area-inset-top)+3.5rem))] w-[16rem] max-w-[74vw] '
      + `${PLAQUE} px-3 py-2 flex flex-col gap-1.5 hidden`;
    this.targetPanel.dataset.hud = 'target';
    this.targetName = document.createElement('div');
    this.targetName.className = `text-[11px] font-semibold text-center truncate ${LABEL}`;
    const targetBar = chunkyBar('h-3.5');
    this.targetFill = targetBar.fill;
    this.targetHp = targetBar.label;
    this.targetHp.classList.add('text-[9px]');
    this.targetRange = document.createElement('div');
    this.targetRange.className = 'text-[10px] font-semibold text-center text-[#ff8a7a] hidden';
    this.targetRange.textContent = '';
    this.targetPanel.append(this.targetName, targetBar.wrap, this.targetRange);
    container.appendChild(this.targetPanel);
  }

  setVitals(v: HudVitals): void {
    const hpFraction = v.hpMax > 0 ? v.hp / v.hpMax : 0;
    this.hpFill.style.width = fillWidth(v.hp, v.hpMax);
    this.hpFill.className = `absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out ${hpTint(hpFraction)}`;
    this.hpLabel.textContent = `${Math.ceil(Math.min(v.hp, v.hpMax))} / ${v.hpMax}`;

    this.staminaFill.style.width = fillWidth(v.stamina, v.staminaMax);
    this.staminaFill.className = `absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out ${STAMINA_TINT}`;

    this.resourceFill.style.width = fillWidth(v.resource, v.resourceMax);
    this.resourceFill.className = 'absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out '
      + `${RESOURCE_TINT[v.resourceKind] ?? RESOURCE_TINT.MANA}`;
    this.resourceGlyph.src = glyphUrl(RESOURCE_GLYPH[v.resourceKind] ?? 'droplet');
    // With the denominator, matching the HP readout. "RAGE 1" leaves the player
    // to guess whether 1 is nearly full or nearly empty — which for rage, a
    // resource that starts at zero and climbs, is the only thing worth knowing.
    const kindLabel = RESOURCE_LABEL[v.resourceKind] ? t(RESOURCE_LABEL[v.resourceKind]) : v.resourceKind;
    this.resourceLabel.textContent = v.resourceMax > 0
      ? `${kindLabel} ${Math.floor(v.resource)} / ${v.resourceMax}`
      : kindLabel;

    const entry = rosterEntry(v.characterClass);
    this.playerPortrait.src = portraitUrl(entry.id);
    this.roleBadge.textContent = ROLE_GLYPH[entry.role];
    this.roleBadge.className = 'absolute -bottom-1 -right-1 w-5 h-5 rounded-full border border-[#514132] '
      + `bg-[#2b2219] flex items-center justify-center text-[11px] leading-none ${ROLE_TINT[entry.role]}`;
    this.playerName.textContent = `${v.nickname || v.characterClass}  ·  ${t(ROLE_LABEL[entry.role])}`;
  }

  /**
   * Rebuilds only what changed: rows are keyed by session id and reused, so a
   * party list refreshed every frame does not thrash the DOM.
   *
   * `members` excludes the local player (they have their own frame) — passing
   * them in anyway is harmless, and the row is drawn dimmer.
   */
  setParty(members: readonly HudPartyMember[]): void {
    const seen = new Set<string>();
    for (const member of members) {
      seen.add(member.sessionId);
      let row = this.partyRows.get(member.sessionId);
      if (!row) row = this.addPartyRow(member.sessionId);

      const fraction = member.hpMax > 0 ? member.hp / member.hpMax : 0;
      const entry = rosterEntry(member.characterClass);
      row.portrait.src = portraitUrl(entry.id);
      row.glyph.textContent = ROLE_GLYPH[entry.role];
      row.glyph.className = `text-[11px] leading-none ${ROLE_TINT[entry.role]}`;
      row.name.textContent = member.nickname || member.characterClass;
      row.fill.style.width = member.isDead ? '0%' : fillWidth(member.hp, member.hpMax);
      row.fill.className = `absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out ${hpTint(fraction)}`;
      const savable = member.reviveSecondsLeft !== null;
      // Three states, not two. A body you can still reach says how long you
      // have and how far it is — the two facts the decision actually turns on.
      // A body past saving says so, plainly, instead of going on advertising a
      // rescue that the server would refuse.
      row.hp.textContent = savable
        ? t('{s}s · {dist}m', { s: member.reviveSecondsLeft ?? 0, dist: Math.round(member.distanceM) })
        : member.isDead ? t('LOST') : `${Math.ceil(member.hp)}`;
      // A skull over the face, not just a word in the bar. Whether a squadmate
      // is down is the one thing in this list worth reading without reading.
      row.skull.classList.toggle('hidden', !member.isDead);
      // A countdown is urgent and a loss is not, so they must not look the
      // same either: the savable row stays legible and pulses, the lost one
      // fades back with the rest of the dead.
      row.hp.classList.toggle('text-warn', savable);
      row.row.classList.toggle('animate-pulse', savable);
      // Dead reads as struck through and grey; far away reads as dimmed. Both
      // are conditions you can do something about, and they are different
      // things, so they must not look the same.
      row.row.classList.toggle('opacity-40', member.isDead && !savable);
      row.row.classList.toggle('opacity-70', !member.isDead && member.distanceM > 15);
      row.row.classList.toggle('ring-2', member.isLocal || !!member.isTargeted);
      row.row.classList.toggle('ring-[#e0a640]', member.isLocal && !member.isTargeted);
      row.row.classList.toggle('ring-[#7fd17a]', !!member.isTargeted);
      row.row.setAttribute('aria-pressed', member.isTargeted ? 'true' : 'false');
    }
    for (const [sessionId, row] of this.partyRows) {
      if (seen.has(sessionId)) continue;
      row.row.remove();
      this.partyRows.delete(sessionId);
    }
    this.partyPanel.classList.toggle('hidden', this.partyRows.size === 0);
  }

  setTarget(target: HudTarget | null): void {
    if (!target) {
      this.targetPanel.classList.add('hidden');
      // Cleared, not just hidden. `hidden` and `invisible` are toggled by
      // different callers (this one and `setVisible`, for the fly-through), and
      // leaving a dead Rattler's name sitting in the DOM is one refactor away
      // from flashing it back onto the screen.
      this.targetName.textContent = '';
      this.targetHp.textContent = '';
      this.targetFill.style.width = '0%';
      this.targetRange.classList.add('hidden');
      return;
    }
    this.targetPanel.classList.remove('hidden');
    const fraction = target.hpMax > 0 ? target.hp / target.hpMax : 0;
    this.targetName.textContent = t('{name}  ·  {dist}m', {
      name: target.name,
      dist: target.distanceM.toLocaleString(getIntlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    });
    this.targetFill.style.width = fillWidth(target.hp, target.hpMax);
    this.targetFill.className = `absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out ${hpTint(fraction)}`;
    this.targetHp.textContent = `${Math.ceil(target.hp)} / ${target.hpMax}`;
    const far = !!target.outOfRange;
    this.targetRange.classList.toggle('hidden', !far);
    if (far) this.targetRange.textContent = t('Out of range');
    this.targetPanel.classList.toggle('ring-2', target.isHostile);
    this.targetPanel.classList.toggle('ring-[#e0483a]', target.isHostile);
  }

  setReticleVisible(visible: boolean): void {
    this.reticle.classList.toggle('hidden', !visible);
  }

  /** Hides everything at once — used for the run-start fly-through, which is a cinematic and owns the screen. */
  setVisible(visible: boolean): void {
    for (const el of [this.reticle, this.playerPanel, this.partyPanel, this.targetPanel]) {
      el.classList.toggle('invisible', !visible);
    }
  }

  private addPartyRow(sessionId: string): NonNullable<ReturnType<typeof this.partyRows.get>> {
    const row = document.createElement('div');
    // 44px tall and pointer-events-auto (2026-10-16): a row is a target button.
    row.className = `${PLAQUE_SMALL} px-1.5 py-1 flex items-center gap-1.5 min-h-[2.75rem] pointer-events-auto cursor-pointer`;
    row.setAttribute('role', 'button');
    row.dataset.partyMember = sessionId;
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.onSelectMember(sessionId);
    });

    const disc = portraitDisc('w-8 h-8');

    const skull = document.createElement('img');
    skull.src = glyphUrl('skull');
    skull.alt = '';
    skull.draggable = false;
    skull.className = 'absolute inset-0 m-auto w-5 h-5 select-none hidden';
    disc.wrap.appendChild(skull);

    const right = document.createElement('div');
    right.className = 'flex-1 min-w-0 flex flex-col gap-1';

    const nameLine = document.createElement('div');
    nameLine.className = 'flex items-center gap-1 min-w-0';
    const glyph = document.createElement('div');
    const name = document.createElement('div');
    name.className = `text-[10px] leading-none font-semibold truncate ${LABEL}`;
    nameLine.append(glyph, name);

    const hpBar = chunkyBar('h-2.5');
    const hp = hpBar.label;
    hp.classList.add('text-[8px]');

    right.append(nameLine, hpBar.wrap);
    row.append(disc.wrap, right);
    this.partyPanel.appendChild(row);

    const entry = { row, name, glyph, portrait: disc.img, fill: hpBar.fill, hp, skull };
    this.partyRows.set(sessionId, entry);
    return entry;
  }

  dispose(): void {
    this.reticle.remove();
    this.playerPanel.remove();
    this.partyPanel.remove();
    this.targetPanel.remove();
  }
}
